import type { SetStateAction } from 'react';

import type { QueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import type { User } from 'cpool-constants';
import {
    AGGREGATE_NAMES,
    EUROPACE_PROCESS_PLATFORMS,
    LOAN_REQUEST_COMMANDS,
    LOAN_REQUEST_OFFER_SOURCES,
} from 'cpool-constants';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import type { UseFormGetValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import type { SourceTypeObject } from '../RequestConfiguration/types';
import type { SchemaCreditRequestForm } from '../schemas/creditRequestForm.schema';
import { buildRequestOffersPayload } from '../utils/buildRequestOffersPayload';
import { verifyBankOffersReceived } from '../utils/verifyBankOffersReceived';
import { APICommunicator } from '~/api/APICommunicator';
import { queryKeys } from '~/utils/queryKeys';

type MutationProps = {
    getValues: UseFormGetValues<SchemaCreditRequestForm>;
    lead: SchemaLoanRequestResolver_Get;
    offerRequestId: string;
    user: User | null;
};

export const useRequestOffersMutation = ({
    queryClient,
    setIsOfferRequestActive,
    updateRequestId,
}: {
    setIsOfferRequestActive: (value: SetStateAction<SourceTypeObject>) => void;
    queryClient: QueryClient;
    updateRequestId: (requestId: string) => void;
}) => {
    const { t } = useTranslation();
    return useMutation({
        mutationFn: ({ getValues, lead, offerRequestId, user }: MutationProps) => {
            return APICommunicator.sendCommand({
                aggregateId: lead.id,
                aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                commandName: LOAN_REQUEST_COMMANDS.requestOffers,
                payload: buildRequestOffersPayload({
                    getValues,
                    lead,
                    offerRequestId,
                    user,
                }),
            });
        },
        onError: () => {
            toast.error(
                t('BANK_CREDIT_REQUEST.OFFER_REQUEST_FAILED', {
                    bank:
                        LOAN_REQUEST_OFFER_SOURCES.EUROPACE[0].toUpperCase() +
                        LOAN_REQUEST_OFFER_SOURCES.EUROPACE.slice(1).toLowerCase(),
                }),
            );
            setIsOfferRequestActive({
                [LOAN_REQUEST_OFFER_SOURCES.EUROPACE]: false,
            });
        },
        onMutate: () => {
            setIsOfferRequestActive({
                [LOAN_REQUEST_OFFER_SOURCES.EUROPACE]: true,
            });
        },
        onSuccess: async (_data, { lead, offerRequestId, user }) => {
            // verifyBankOffersReceived calls are not awaited so we can start verification for a few banks in parallel
            const europacePromise = verifyBankOffersReceived({
                bank: LOAN_REQUEST_OFFER_SOURCES.EUROPACE,
                lead,
                offerRequestId,
                queryClient,
                setIsOfferRequestActive,
                t,
                updateRequestId,
            });

            const missingPartnerIds: Array<string> = [];
            if (
                (lead.europace?.platform === EUROPACE_PROCESS_PLATFORMS.EUROPACE2 &&
                    !user?.europacePlatforms?.europace2.partnerId) ||
                (lead.europace?.platform === EUROPACE_PROCESS_PLATFORMS.STARPOOL &&
                    !user?.europacePlatforms?.starpool.partnerId)
            ) {
                missingPartnerIds.push(t('TOAST.EUROPACE_PARTNER_ID'));
            }

            // needs to be increased when adding new banks
            if (missingPartnerIds.length === 1) {
                toast.error(t('TOAST.NO_PARTNER_IDS'));
            } else if (missingPartnerIds.length > 0) {
                toast.info(`${t('TOAST.MISSING_PARTNER_ID')} ${missingPartnerIds.join(', ')}`);
            }

            await Promise.all([europacePromise]);
            queryClient.invalidateQueries({ queryKey: queryKeys.get(lead.id) });
        },
    });
};
